import type { Point, Rect, StealthLevelResult, StealthStarRating } from './types';

export function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, value));
}

export function rectsOverlap(left: Rect, right: Rect): boolean {
  return left.x < right.x + right.w
    && left.x + left.w > right.x
    && left.y < right.y + right.h
    && left.y + left.h > right.y;
}

export function pointInRect(point: Point, rect: Rect): boolean {
  return point.x >= rect.x && point.x <= rect.x + rect.w
    && point.y >= rect.y && point.y <= rect.y + rect.h;
}

export function rectCenter(rect: Rect): Point {
  return { x: rect.x + rect.w / 2, y: rect.y + rect.h / 2 };
}

export function distanceBetween(left: Point, right: Point): number {
  return Math.hypot(right.x - left.x, right.y - left.y);
}

export function normalizeAngle(angle: number): number {
  let normalized = angle;
  while (normalized > Math.PI) normalized -= Math.PI * 2;
  while (normalized < -Math.PI) normalized += Math.PI * 2;
  return normalized;
}

export function pointInVisionCone(origin: Point, angle: number, target: Point, distance: number, fieldOfView: number): boolean {
  const offsetX = target.x - origin.x;
  const offsetY = target.y - origin.y;
  if (Math.hypot(offsetX, offsetY) > distance) return false;
  const targetAngle = Math.atan2(offsetY, offsetX);
  return Math.abs(normalizeAngle(targetAngle - angle)) <= fieldOfView / 2;
}

export function segmentIntersectsRect(start: Point, end: Point, rect: Rect): boolean {
  if (pointInRect(start, rect) || pointInRect(end, rect)) return true;
  const deltaX = end.x - start.x;
  const deltaY = end.y - start.y;
  let near = 0;
  let far = 1;
  const edges = [
    [-deltaX, start.x - rect.x],
    [deltaX, rect.x + rect.w - start.x],
    [-deltaY, start.y - rect.y],
    [deltaY, rect.y + rect.h - start.y],
  ] as const;

  for (const [direction, offset] of edges) {
    if (direction === 0) {
      if (offset < 0) return false;
      continue;
    }
    const ratio = offset / direction;
    if (direction < 0) near = Math.max(near, ratio);
    else far = Math.min(far, ratio);
    if (near > far) return false;
  }
  return true;
}

export function hasLineOfSight(start: Point, end: Point, blockers: readonly Rect[]): boolean {
  return !blockers.some((blocker) => segmentIntersectsRect(start, end, blocker));
}

export function findGridPath(
  start: Point,
  end: Point,
  blockers: readonly Rect[],
  worldWidth: number,
  worldHeight: number,
  clearance = 18,
  step = 18,
): Point[] {
  const columns = Math.floor(worldWidth / step) + 1;
  const rows = Math.floor(worldHeight / step) + 1;
  const toCell = (point: Point) => ({
    x: clamp(Math.round(point.x / step), 0, columns - 1),
    y: clamp(Math.round(point.y / step), 0, rows - 1),
  });
  const toPoint = (cell: { x: number; y: number }): Point => ({ x: cell.x * step, y: cell.y * step });
  const startCell = toCell(start);
  const endCell = toCell(end);
  const startKey = `${startCell.x},${startCell.y}`;
  const endKey = `${endCell.x},${endCell.y}`;
  const blocked = (cell: { x: number; y: number }) => {
    const point = toPoint(cell);
    return blockers.some((blocker) => pointInRect(point, {
      x: blocker.x - clearance,
      y: blocker.y - clearance,
      w: blocker.w + clearance * 2,
      h: blocker.h + clearance * 2,
    }));
  };
  const queue = [startCell];
  const parents = new Map<string, string | null>([[startKey, null]]);
  for (let index = 0; index < queue.length; index += 1) {
    const current = queue[index];
    const currentKey = `${current.x},${current.y}`;
    if (currentKey === endKey) break;
    for (const [offsetX, offsetY] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
      const next = { x: current.x + offsetX, y: current.y + offsetY };
      const key = `${next.x},${next.y}`;
      if (next.x < 0 || next.x >= columns || next.y < 0 || next.y >= rows || parents.has(key)) continue;
      if (key !== endKey && blocked(next)) continue;
      parents.set(key, currentKey);
      queue.push(next);
    }
  }
  if (!parents.has(endKey)) return [];
  const cells: Array<{ x: number; y: number }> = [];
  let cursor: string | null = endKey;
  while (cursor) {
    const [x, y] = cursor.split(',').map(Number);
    cells.push({ x, y });
    cursor = parents.get(cursor) ?? null;
  }
  cells.reverse();
  const path: Point[] = [];
  for (let index = 1; index < cells.length; index += 1) {
    const previous = cells[index - 1];
    const current = cells[index];
    const next = cells[index + 1];
    if (next && current.x - previous.x === next.x - current.x && current.y - previous.y === next.y - current.y) continue;
    path.push(toPoint(current));
  }
  if (path.length === 0 || distanceBetween(path[path.length - 1], end) > 1) path.push(end);
  else path[path.length - 1] = end;
  return path;
}

export function calculateStealthStars(
  clearSeconds: number,
  retries: number,
  alerts: number,
  targetSeconds: number,
): StealthStarRating {
  const timePenalty = clearSeconds > targetSeconds * 1.55 ? 2 : clearSeconds > targetSeconds ? 1 : 0;
  const retryPenalty = retries >= 3 ? 2 : retries >= 1 ? 1 : 0;
  const alertPenalty = alerts >= 4 ? 2 : alerts >= 2 ? 1 : 0;
  return Math.max(1, 3 - Math.min(2, timePenalty + retryPenalty + alertPenalty)) as StealthStarRating;
}

export function isBetterStealthResult(candidate: StealthLevelResult, previous?: StealthLevelResult): boolean {
  if (!previous) return true;
  if (candidate.stars !== previous.stars) return candidate.stars > previous.stars;
  if (candidate.alerts !== previous.alerts) return candidate.alerts < previous.alerts;
  if (candidate.retries !== previous.retries) return candidate.retries < previous.retries;
  return candidate.clearSeconds < previous.clearSeconds;
}

export function formatStealthSeconds(seconds: number): string {
  return `${Math.max(0, seconds).toFixed(1)}초`;
}
